/**
 * TasksSidebarLink.tsx — Tasks link for the Command Center sidebar.
 *
 * Renders a "Tasks" navigation link between existing Command Center sidebar
 * entries, with an active-task badge driven by the taskThreads Redux slice.
 *
 * Usage: import into the relevant Command Center nav list and render between
 * existing sidebar entries per the existing Command Center navigation model.
 *
 * Spec 017 FR-015.
 */

import * as React from 'react'
import { useSelector } from 'react-redux'
import { tasksPath } from '@logic'

// Minimal selector — avoids importing the full RootState type
function selectTaskCount(state: any): number {
  const tt = state?.taskThreads
  if (!tt) return 0
  const order = Array.isArray(tt.order) ? tt.order : []
  const items =
    tt.items && typeof tt.items === 'object'
      ? tt.items
      : {}

  return order.filter((id: string) => {
    const item = items[id]
    return item && item.status !== 'archived'
  }).length
}

interface TasksSidebarLinkProps {
  /** onClick handler — typically calls history.push(tasksPath) */
  onClick?: () => void
  /** Whether this item is currently active */
  active?: boolean
  /** Additional class name */
  className?: string
  style?: React.CSSProperties
}

export const TasksSidebarLink: React.FC<TasksSidebarLinkProps> = ({
  onClick,
  active = false,
  className,
  style,
}) => {
  const taskCount = useSelector(selectTaskCount)

  return (
    <button
      type="button"
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '8px 16px',
        cursor: 'pointer',
        background: active ? 'rgba(0,0,0,0.08)' : 'transparent',
        border: 'none',
        borderRadius: 4,
        color: 'inherit',
        font: 'inherit',
        fontWeight: active ? 600 : 400,
        textAlign: 'left',
        ...style,
      }}
      onClick={onClick}
      aria-label={`Tasks${taskCount > 0 ? ` — ${taskCount} active` : ''}`}
      aria-current={active ? 'page' : undefined}
    >
      {/* Task icon (folder / checklist) */}
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M5 7.5h6M5 10h4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
        <path d="M5 5h6" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
      </svg>
      <span style={{ flex: 1 }}>Tasks</span>
      {taskCount > 0 && (
        <span
          style={{
            background: '#3b82f6',
            color: '#fff',
            borderRadius: 10,
            padding: '1px 7px',
            fontSize: 11,
            fontWeight: 600,
            minWidth: 18,
            textAlign: 'center',
          }}
          aria-label={`${taskCount} active tasks`}
        >
          {taskCount > 99 ? '99+' : taskCount}
        </span>
      )}
    </button>
  )
}

export { tasksPath }
export default TasksSidebarLink
